<template>

    <div id="create-report">

        <div class="flex">
            <div class="flex-1 mb-4">
                <h1 class="text-2xl font-medium text-gray-900 capitalize">
                    {{ reportManageTitle }}
                </h1>
            </div>
            <div class="flex mb-4 justify-end">
                <router-link :to="{ name: 'report' }" custom v-slot="{ navigate }">
                    <button @click="navigate" class="btn-outline ml-6">
                        Go Back
                    </button>
                </router-link>
            </div>
        </div>

        <div class="flex flex-col" v-if="loaded">

            <ul class="top-help-block">
                <li><span class="text-brand-600">*</span> denotes required field</li>
            </ul>

            <form action="#" method="POST" @submit.prevent="form.submitReportManagerForm()">

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Report Information</h3>
                            <p>
                                Settings pertaining to how a report is rendered and the report information. When a report is
                                rendered two primary sources of information are available, the report data (individual rows
                                of information based on the report query) and the report totals (the counts/sums of columns
                                on the report). Columns are configured by the report selects and can be configured to count and
                                sum relevant columns (please note these columns are limited to numeric data types only).
                            </p>
                        </div>
                        <div class="panel-section">

                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('name') }">
                                    <label for="report_name" class="block text-sm font-medium text-gray-700">Name<i></i></label>
                                    <input type="text" name="name" id="report_name" v-model="form.data.name" required>
                                    <span v-if="form.fieldHasErrors('name')">{{ form.getFieldError('name') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('connection') }">
                                    <label for="report_connection" class="block text-sm font-medium text-gray-700">Connection<i></i></label>
                                    <select name="connection" id="report_connection" v-model="form.data.connection" required>
                                        <option :value="null">Select Connection</option>
                                        <option :value="connection.value" v-for="connection in form.connections">{{ connection.name }}</option>
                                    </select>
                                    <span v-if="form.fieldHasErrors('connection')">{{ form.getFieldError('connection') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('description') }">
                                    <label for="report_description" class="block text-sm font-medium text-gray-700">Description<i></i></label>
                                    <input type="text" name="description" id="report_description" v-model="form.data.description" required>
                                    <span v-if="form.fieldHasErrors('description')">{{ form.getFieldError('description') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('display_limit') }">
                                    <label for="report_display_limit" class="block text-sm font-medium text-gray-700">Display Limit<i></i></label>
                                    <input type="text" name="display_limit" id="report_display_limit" v-model="form.data.display_limit" required>
                                    <span v-if="form.fieldHasErrors('display_limit')">{{ form.getFieldError('display_limit') }}</span>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex items-center">
                                        <input id="show_data" name="show_data" type="checkbox" class="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded" value="1" v-model="form.data.show_data">
                                        <label for="show_data" class="ml-2 block text-sm text-gray-900">
                                            Display Report Data
                                        </label>
                                    </div>
                                    <p class="text-sm text-gray-400 mt-2">
                                        Should this report display the result data when viewing the report.
                                    </p>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex items-center">
                                        <input id="show_totals" name="show_totals" type="checkbox" class="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded" value="1" v-model="form.data.show_totals">
                                        <label for="show_totals" class="ml-2 block text-sm text-gray-900">
                                            Display Report Totals
                                        </label>
                                    </div>
                                    <p class="text-sm text-gray-400 mt-2">
                                        Should this report display the result totals when viewing the report.
                                    </p>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex items-center">
                                        <input id="admin_only" name="admin_only" type="checkbox" class="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded" value="1" v-model="form.data.admin_only">
                                        <label for="admin_only" class="ml-2 block text-sm text-gray-900">
                                            Admin Only
                                        </label>
                                    </div>
                                    <p class="text-sm text-gray-400 mt-2">
                                        Should this report only be visible to admins.
                                    </p>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex items-center">
                                        <input id="active" name="active" type="checkbox" class="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded" value="1" v-model="form.data.active">
                                        <label for="active" class="ml-2 block text-sm text-gray-900">
                                            Active
                                        </label>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Middleware</h3>
                            <p>
                                Middleware provides a way to automatically inject query conditions into a report or intercept
                                a request to render a report. For example check that a user is authenticated to view the report
                                or check that results shown are owned by the current user.
                            </p>
                        </div>
                        <div class="panel-section xl:relative">
                            <div class="flex pb-4 justify-end xl:pb-0 xl:absolute xl:top-0 xl:right-0">
                                <button type="button" class="btn-outline" @click.prevent="form.addMiddleware()">Add Middleware</button>
                            </div>

                            <div v-if="form.hasMiddleware()">

                                <div class="grid-form-control pb-6 border-b " :class="{ 'form-control-error': form.fieldHasErrors('middleware') }" v-if="middleware.deleted_at === null" v-for="(middleware, index) in form.data.middleware">
                                    <label :for="'report_middleware[' + index + ']'" class="font-medium text-gray-700">Middleware<i></i></label>
                                    <select name="middleware" :id="'report_middleware[' + index + ']'" v-model="form.data.middleware[index].middleware" required>
                                        <option :value="null">Select Middleware</option>
                                        <option :value="middleware.value" v-for="middleware in form.middleware">{{ middleware.name }}</option>
                                    </select>
                                    <button type="button" class="btn-error" @click.prevent="form.removeMiddleware(index)">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 xl:h-5 xl:w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                    <span v-if="form.fieldHasErrors('middleware')">{{ form.getFieldError('middleware') }}</span>
                                </div>

                            </div>
                            <div class="grid grid-cols-6 gap-6" v-else>
                                <div class="col-span-6 p-4">
                                    <div class="text-gray-200 my-6">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                        </svg>
                                    </div>
                                    <h2 class="text-gray-600 font-medium text-2xl text-center">No Middleware</h2>
                                    <p class="text-center mt-4 text-sm text-gray-500">
                                        This report does not have any middleware associated with it.
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Fields</h3>
                            <p>
                                Report fields are the parameters passed to the report, these parameters are used to further
                                refine rendered report data e.g. passing a start_date and end_date to limit results between
                                two dates.
                            </p>
                        </div>
                        <div class="panel-section xl:relative">
                            <div class="flex pb-4 justify-end xl:pb-0 xl:absolute xl:top-0 xl:right-0">
                                <button type="button" class="btn-outline" @click.prevent="form.addField()">Add Fields</button>
                            </div>

                            <div v-if="form.hasFields()">

                                <div v-if="field.deleted_at === null" v-for="(field, index) in form.data.fields">
                                    <div class="grid-form-control" :class="{ 'form-control-error': form.fieldHasErrors('label') }">
                                        <label for="field_label" class="font-medium text-gray-700">Field Label<i></i></label>
                                        <input type="text" name="label" id="field_label" v-model="form.data.fields[index].label" required>
                                        <button type="button" class="btn-error" @click.prevent="form.removeField(index)">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 xl:h-5 xl:w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                        <span v-if="form.fieldHasErrors('label')">{{ form.getFieldError('label') }}</span>
                                    </div>

                                    <div class="grid grid-cols-6 gap-6 mb-6">
                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('alias') }">
                                            <label for="field_alias" class="font-medium text-gray-700">Criteria Alias<i></i></label>
                                            <input type="text" name="alias" id="field_alias" v-model="form.data.fields[index].alias" required>
                                            <span v-if="form.fieldHasErrors('alias')">{{ form.getFieldError('alias') }}</span>
                                        </div>

                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('type') }">
                                            <label for="field_type" class="font-medium text-gray-700">Criteria Type<i></i></label>
                                            <select name="type" id="field_type" v-model="form.data.fields[index].type" required>
                                                <option :value="type.value" v-for="type in form.field_types">{{ type.name }}</option>
                                            </select>
                                            <span v-if="form.fieldHasErrors('type')">{{ form.getFieldError('type') }}</span>
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-6 gap-6 mb-6" v-if="form.data.fields[index].type === 'select'">
                                        <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('model') }">
                                            <label for="field_model" class="font-medium text-gray-700">Criteria Type<i></i></label>
                                            <select name="model" id="field_model" v-model="form.data.fields[index].model" required>
                                                <option :value="null">Select Model</option>
                                                <option :value="type.value" v-for="type in form.models">{{ type.name }}</option>
                                            </select>
                                            <span v-if="form.fieldHasErrors('model')">{{ form.getFieldError('model') }}</span>
                                        </div>

                                        <div class="hidden xl:flex form-control col-span-6 xl:col-span-2">&nbsp;</div>

                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('model_select_value') }">
                                            <label for="field_model_select_value" class="font-medium text-gray-700">Criteria Value<i></i></label>
                                            <input type="text" name="model_select_value" id="field_model_select_value" v-model="form.data.fields[index].model_select_value" required>
                                            <span v-if="form.fieldHasErrors('model_select_value')">{{ form.getFieldError('model_select_value') }}</span>
                                        </div>

                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('model_select_name') }">
                                            <label for="field_model_select_name" class="font-medium text-gray-700">Criteria Name<i></i></label>
                                            <input type="text" name="model_select_name" id="field_model_select_name" v-model="form.data.fields[index].model_select_name" required>
                                            <span v-if="form.fieldHasErrors('model_select_name')">{{ form.getFieldError('model_select_name') }}</span>
                                        </div>


                                    </div>

                                    <hr class="my-4">
                                </div>

                            </div>
                            <div class="grid grid-cols-6 gap-6" v-else>
                                <div class="col-span-6 p-4">
                                    <div class="text-gray-200 my-6">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                    </div>
                                    <h2 class="text-gray-600 font-medium text-2xl text-center">No Fields</h2>
                                    <p class="text-center mt-4 text-sm text-gray-500">
                                        This report does not have any fields associated with it.
                                    </p>
                                </div>
                            </div>


                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Columns</h3>
                            <p>
                                Add some columns to the report structure, columns are the table head items in the report.
                            </p>
                        </div>
                        <div class="panel-section xl:relative">
                            <div class="flex pb-4 justify-end xl:pb-0 xl:absolute xl:top-0 xl:right-0">
                                <button type="button" class="btn-outline" @click.prevent="form.addSelect()">Add Columns</button>
                            </div>

                            <div v-if="form.hasSelects()">

                                <div v-if="select.deleted_at === null" v-for="(select, index) in form.data.selects">
                                    <div class="grid-form-control" :class="{ 'form-control-error': form.fieldHasErrors('type') }">
                                        <label for="select_type" class="font-medium text-gray-700">Field Type<i></i></label>
                                        <select name="type" id="select_type" v-model="form.data.selects[index].type" required>
                                            <option :value="type.value" v-for="type in form.data_types">{{ type.name }}</option>
                                        </select>
                                        <button type="button" class="btn-error" @click.prevent="form.removeSelect(index)">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 xl:h-5 xl:w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                        <span v-if="form.fieldHasErrors('type')">{{ form.getFieldError('type') }}</span>
                                    </div>

                                    <div class="grid grid-cols-6 gap-6 mb-6">
                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('column') }">
                                            <label for="select_column" class="font-medium text-gray-700">Criteria Column<i></i></label>
                                            <input type="text" name="column" id="select_column" v-model="form.data.selects[index].column" required>
                                            <span v-if="form.fieldHasErrors('column')">{{ form.getFieldError('column') }}</span>
                                        </div>

                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('alias') }">
                                            <label for="select_alias" class="font-medium text-gray-700">Criteria Alias<i></i></label>
                                            <input type="text" name="alias" id="select_alias" v-model="form.data.selects[index].alias" required>
                                            <span v-if="form.fieldHasErrors('alias')">{{ form.getFieldError('alias') }}</span>
                                        </div>
                                        <div class="col-span-2 py-8">
                                            <button class="btn" :class="{ 'btn-outline': select.column_order === 1 }" :disabled="select.column_order === 1" @click.prevent="form.moveSelectUp(index)">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 xl:h-5 xl:w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z" />
                                                </svg>
                                            </button>
                                            <button class="btn" :class="{ 'btn-outline': select.column_order === form.data.selects.length }" :disabled="select.column_order === form.data.selects.length" @click.prevent="form.moveSelectDown(index)">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 xl:h-5 xl:w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z" />
                                                </svg>
                                            </button>

                                        </div>
                                    </div>

                                    <hr class="my-4">
                                </div>

                            </div>
                            <div class="grid grid-cols-6 gap-6" v-else>
                                <div class="col-span-6 p-4">
                                    <div class="text-gray-200 my-6">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                                        </svg>
                                    </div>
                                    <h2 class="text-gray-600 font-medium text-2xl text-center">No Columns</h2>
                                    <p class="text-center mt-4 text-sm text-gray-500">
                                        This report does not have any columns associated with it.
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>


                <div class="panel">
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Table (from)</h3>
                            <p>
                                Select the primary table the report will be querying.
                            </p>
                        </div>
                        <div class="panel-section">

                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('table') }">
                                    <label for="report_table" class="block text-sm font-medium text-gray-700">Table<i></i></label>
                                    <input type="text" name="table" id="report_table" v-model="form.data.table" required>
                                    <span v-if="form.fieldHasErrors('table')">{{ form.getFieldError('table') }}</span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Joins</h3>
                            <p>
                                Join other tables through the primary table or joined tables.
                            </p>
                        </div>
                        <div class="panel-section xl:relative">
                            <div class="flex pb-4 justify-end xl:pb-0 xl:absolute xl:top-0 xl:right-0">
                                <button type="button" class="btn-outline" @click.prevent="form.addJoin()">Add Joins</button>
                            </div>

                            <div v-if="form.hasJoins()">

                                <div v-if="join.deleted_at === null" v-for="(join, index) in form.data.joins">

                                    <div class="grid-form-control" :class="{ 'form-control-error': form.fieldHasErrors('type') }">
                                        <label for="join_type" class="font-medium text-gray-700">Join Type<i></i></label>
                                        <select name="type" id="join_type" v-model="form.data.joins[index].type" required>
                                            <option :value="type.value" v-for="type in form.join_types">{{ type.name }}</option>
                                        </select>
                                        <button type="button" class="btn-error" @click.prevent="form.removeJoin(index)">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 xl:h-5 xl:w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                        <span v-if="form.fieldHasErrors('type')">{{ form.getFieldError('type') }}</span>
                                    </div>

                                    <div class="grid grid-cols-6 gap-6 mb-6">
                                        <div class="form-control col-span-6 xl:col-span-5" :class="{ 'form-control-error': form.fieldHasErrors('table') }">
                                            <label for="join_table" class="font-medium text-gray-700">Join Table<i></i></label>
                                            <input type="text" name="table" id="join_table" v-model="form.data.joins[index].table" required>
                                            <span v-if="form.fieldHasErrors('table')">{{ form.getFieldError('table') }}</span>
                                        </div>

                                        <div class="hidden xl:flex form-control col-span-6 xl:col-span-1">&nbsp;</div>

                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('first') }">
                                            <label for="join_first" class="font-medium text-gray-700">Column 1<i></i></label>
                                            <input type="text" name="first" id="join_first" v-model="form.data.joins[index].first" required>
                                            <span v-if="form.fieldHasErrors('first')">{{ form.getFieldError('first') }}</span>
                                        </div>

                                        <div class="form-control col-span-6 xl:col-span-1" :class="{ 'form-control-error': form.fieldHasErrors('operator') }">
                                            <label for="join_operator" class="font-medium text-gray-700">Operator<i></i></label>
                                            <input type="text" name="operator" id="join_operator" v-model="form.data.joins[index].operator" required>
                                            <span v-if="form.fieldHasErrors('operator')">{{ form.getFieldError('operator') }}</span>
                                        </div>

                                        <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('second') }">
                                            <label for="join_second" class="font-medium text-gray-700">Column 1<i></i></label>
                                            <input type="text" name="second" id="join_second" v-model="form.data.joins[index].second" required>
                                            <span v-if="form.fieldHasErrors('second')">{{ form.getFieldError('second') }}</span>
                                        </div>
                                    </div>

                                    <hr class="my-4">
                                </div>

                            </div>
                            <div class="grid grid-cols-6 gap-6" v-else>
                                <div class="col-span-6 p-4">
                                    <div class="text-gray-200 my-6">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                    </div>
                                    <h2 class="text-gray-600 font-medium text-2xl text-center">No Joins</h2>
                                    <p class="text-center mt-4 text-sm text-gray-500">
                                        This report does not have any joins associated with it.
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Where</h3>
                            <p>
                                Columns on the primary table or any joined tables can be used in this section e.g.
                                <span class="text-xs font-medium bg-brand-50 p-1 text-brand-800">users.active = 1</span>.
                                Field parameters can be used by adding the criteria alias in curly braces e.g.
                                <span class="text-xs font-medium bg-brand-50 p-1 text-brand-800">users.created_at >= {start_date}</span>.
                                We recommend always appending the columns table to avoid ambiguous column names
                                e.g. <span class="text-xs font-medium bg-brand-50 p-1 text-brand-800">users.id = 1 AND logs.id > 100</span>
                            </p>
                        </div>
                        <div class="panel-section">

                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('where') }">
                                    <label for="report_where" class="font-medium text-gray-700">Where</label>
                                    <textarea id="report_where" name="where" rows="8" v-model="form.data.where"></textarea>
                                    <span v-if="form.fieldHasErrors('where')">{{ form.getFieldError('where') }}</span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Sorting</h3>
                            <p>
                                Sort you results.
                            </p>
                        </div>
                        <div class="panel-section">

                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('groupby') }">
                                    <label for="report_groupby" class="block text-sm font-medium text-gray-700">Group By</label>
                                    <input type="text" name="groupby" id="report_groupby" v-model="form.data.groupby" placeholder="(e.g. users.role)">
                                    <span v-if="form.fieldHasErrors('groupby')">{{ form.getFieldError('groupby') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('having') }">
                                    <label for="report_having" class="block text-sm font-medium text-gray-700">Having</label>
                                    <input type="text" name="having" id="report_having" v-model="form.data.having" placeholder="(e.g. users.id > 100)">
                                    <span v-if="form.fieldHasErrors('having')">{{ form.getFieldError('having') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('orderby') }">
                                    <label for="report_orderby" class="block text-sm font-medium text-gray-700">Order By</label>
                                    <input type="text" name="orderby" id="report_orderby" v-model="form.data.orderby" placeholder="(e.g. users.created_at DESC)">
                                    <span v-if="form.fieldHasErrors('orderby')">{{ form.getFieldError('orderby') }}</span>
                                </div>
                            </div>


                        </div>
                    </div>
                </div>

                <div class="flex justify-end">
                    <div class="flex border-l-4 border-brand-500 bg-white text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-brand-400" v-if="form.successful()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <p class="text-gray-700 text-sm" v-html="form.getSuccessMessage()"></p>
                    </div>

                    <div class="flex border-l-4 border-error-500 bg-error-50 text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-error-400" v-if="form.hasError()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <p class="text-error-500 text-sm" v-html="form.getErrorMessage()"></p>
                    </div>

                    <button type="submit" class="btn-outline justify-center px-6 ml-6" @click.prevent="goBack">
                        Go Back
                    </button>

                    <button type="submit" class="btn justify-center px-6 ml-6" :disabled="form.isFormUpdating()">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="form.isFormUpdating()">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ reportManageTitle }}
                    </button>
                </div>

            </form>

        </div>

    </div>

</template>

<script>
import {Report} from "../../app/Report/Report";
import Loading from "../Support/Loading";
import {NavigationMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "CreateEditReport",
    mixins: [PermissionMixin, NavigationMixin],
    data() {
        return {
            report: null,
            form: null,
            loaded: false,
        }
    },
    components: {
        Loading
    },
    computed: {
        reportManageTitle() {
            if (this.report) {
                return 'Edit Report';
            }

            return 'Create Report';
        }
    },
    mounted() {
        let vm = this;
        let data = {};

        new Promise(resolve => {
            vm.report = this.$router.currentRoute.params.report;

            vm.form = new Report(data);

            if (vm.report) {
                vm.form.showManageReport(vm.report);
            }

            vm.form.loadSettings().then(response => {


                resolve(true);
            }).catch(error => vm.handleError(error));

        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>


